import { useEffect, useMemo, useRef, useState } from "react";
import { serverFetch, apiUrl } from "@server";
import { Checkbox, DatePicker, Input, message, Space, Spin, Table, Tag, Button } from "antd";
import { CheckOutlined, CloseOutlined, DownloadOutlined, FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { fmtDateTimeFull } from "@extensions/utils";
import { backupStatuses } from "@extensions/enums";
import { useSelector } from "react-redux";
import { strDate2Server } from "@extensions/convert";
import { refreshButton } from "@extensions/design";
import dayjs from "dayjs";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";

const CompanyBackups = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [allCompanies, setAllCompanies] = useState(false);
  const [onlyActive, setOnlyActive] = useState(true);
  const [searchStr, setSearchStr] = useState("");
  const user = useSelector(s => s.user);
  const tableWrapRef = useRef(null);
  const [tableScrollY, setTableScrollY] = useState(300);
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftDateFrom, setDraftDateFrom] = useState(null);
  const [draftDateTo, setDraftDateTo] = useState(null);
  const [draftAllCompanies, setDraftAllCompanies] = useState(false);
  const [draftOnlyActive, setDraftOnlyActive] = useState(true);

  const loadDatesFromStorage = () => {
    try {
      const stored = localStorage.getItem("companyBackups");
      if (stored) {
        const data = JSON.parse(stored);
        if (data.dateFrom && data.dateTo) {
          return {
            dateFrom: dayjs(data.dateFrom),
            dateTo: dayjs(data.dateTo),
          };
        }
      }
    } catch (e) {
      console.error("Error loading dates from localStorage:", e);
    }
    const today = dayjs();
    return {
      dateFrom: today.startOf("day"),
      dateTo: today.endOf("day"),
    };
  };

  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  useEffect(() => {
    const dates = loadDatesFromStorage();
    setDateFrom(dates.dateFrom);
    setDateTo(dates.dateTo);
  }, []);

  const saveDatesToStorage = (from, to) => {
    try {
      const data = {
        dateFrom: from ? from.toISOString() : null,
        dateTo: to ? to.toISOString() : null,
      };
      localStorage.setItem("companyBackups", JSON.stringify(data));
    } catch (e) {
      console.error("Error saving dates to localStorage:", e);
    }
  };

  const handleDateFromChange = value => {
    setDateFrom(value);
    if (value && dateTo) {
      saveDatesToStorage(value, dateTo);
    } else if (!value) {
      saveDatesToStorage(null, null);
    }
  };

  const handleDateToChange = value => {
    setDateTo(value);
    if (dateFrom && value) {
      saveDatesToStorage(dateFrom, value);
    } else if (!value) {
      saveDatesToStorage(null, null);
    }
  };

  const getData = () => {
    if (!dateFrom || !dateTo) return;

    setLoading(true);
    const dateFromStr = strDate2Server(dateFrom);
    const dateToStr = strDate2Server(dateTo);
    serverFetch(
      `company/Backup?allCompanies=${allCompanies}&onlyActive=${onlyActive}&dateFrom=${dateFromStr}&dateTo=${dateToStr}`
    )
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка загрузки");
      });
  };

  useEffect(() => {
    getData();
  }, [allCompanies, onlyActive, dateFrom, dateTo]);

  useEffect(() => {
    if (isMobile) return;
    const updateTableHeight = () => {
      const top = tableWrapRef.current?.getBoundingClientRect?.().top ?? 0;
      const viewportHeight = Math.min(window.innerHeight, document.documentElement.clientHeight);
      const next = Math.max(200, viewportHeight - top - 32);
      setTableScrollY(next);
    };

    updateTableHeight();
    window.addEventListener("resize", updateTableHeight);
    return () => window.removeEventListener("resize", updateTableHeight);
  }, [isMobile]);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (dateFrom && dateTo) {
      parts.push(`${dateFrom.format("DD.MM.YYYY")} — ${dateTo.format("DD.MM.YYYY")}`);
    } else if (dateFrom) parts.push(`с ${dateFrom.format("DD.MM.YYYY")}`);
    else if (dateTo) parts.push(`по ${dateTo.format("DD.MM.YYYY")}`);
    else parts.push("период не задан");
    if (user?.admin && allCompanies) parts.push("все компании");
    if (onlyActive) parts.push("только активные");
    return parts.join(" · ");
  }, [dateFrom, dateTo, allCompanies, onlyActive, user?.admin]);

  const openFilterModal = () => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setDraftAllCompanies(allCompanies);
    setDraftOnlyActive(onlyActive);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (!draftDateFrom || !draftDateTo) {
      message.warning("Укажите период дат");
      return;
    }
    setDateFrom(draftDateFrom);
    setDateTo(draftDateTo);
    saveDatesToStorage(draftDateFrom, draftDateTo);
    setAllCompanies(draftAllCompanies);
    setOnlyActive(draftOnlyActive);
    setFilterOpen(false);
  };

  const cancelFilter = () => setFilterOpen(false);

  const formatBytes = bytes => {
    if (!bytes) return "0 МБ";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} МБ`;
  };

  /** Только число в МБ (без единицы), для карточки мобилки */
  const formatMbNumber = bytes => {
    if (!bytes) return "0";
    const mb = bytes / (1024 * 1024);
    return mb.toFixed(2);
  };

  /** Мобилка: сегодня — только время; вчера — «Вчера» + время; иначе полная дата */
  const formatCreatedOnMobile = value => {
    if (!value) return "—";
    const d = dayjs(value);
    const today = dayjs().startOf("day");
    const yesterday = dayjs().subtract(1, "day").startOf("day");
    const dDay = d.startOf("day");
    if (dDay.isSame(today, "day")) return d.format("HH:mm:ss");
    if (dDay.isSame(yesterday, "day")) return `Вчера ${d.format("HH:mm:ss")}`;
    return fmtDateTimeFull(value);
  };

  const renderStatusTag = status => {
    const statusItem = backupStatuses.find(s => s.value === status);
    if (!statusItem) return "-";
    return <Tag color={statusItem.color}>{statusItem.label}</Tag>;
  };

  const handleDownload = async id => {
    const sessionId = localStorage.getItem("sessionId");
    const url = `${apiUrl}/api/company/Backup/Download/${id}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: sessionId ? { SessionId: sessionId } : {},
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { userMessage: "Ошибка скачивания файла" };
        }
        throw errorData;
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      message.error(e.userMessage || "Ошибка при скачивании файла");
    }
  };

  const columns = [
    { dataIndex: "id", title: "#", width: 80 },
    {
      dataIndex: "createdOn",
      title: "Дата время",
      width: 180,
      render: value => (value ? fmtDateTimeFull(value) : "-"),
    },
    {
      dataIndex: "size",
      title: "Размер",
      width: 120,
      render: formatBytes,
    },
    {
      dataIndex: "sizeTake",
      title: "Передано",
      width: 120,
      render: formatBytes,
    },
    { dataIndex: "backupGuid", title: "Код", width: 200 },
    { dataIndex: "companyName", title: "Компания", width: 200 },
    {
      dataIndex: "status",
      title: "Статус",
      width: 180,
      render: status => renderStatusTag(status),
    },
    {
      title: "",
      width: 100,
      render: (_, record) => {
        if ([2, 3, 4].includes(record.status)) {
          return (
            <Button
              type="link"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record.id)}
              style={{ opacity: 0 }}
              className="backup-download-btn"
            >
              Скачать
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const filtered = items.filter(i => {
    if (!searchStr) return true;
    const search = searchStr.toLowerCase();
    const companyName = (i.companyName || "").toLowerCase();
    const backupGuid = (i.backupGuid || "").toLowerCase();
    const id = String(i.id || "").toLowerCase();
    return companyName.includes(search) || backupGuid.includes(search) || id.includes(search);
  });

  const desktopTableBlock = (
    <div ref={tableWrapRef}>
      <Table
        dataSource={filtered}
        loading={loading}
        columns={columns}
        rowKey="id"
        pagination={false}
        size="small"
        scroll={{ x: "max-content", y: tableScrollY }}
        onRow={record => ({
          onMouseEnter: e => {
            const btn = e.currentTarget.querySelector(".backup-download-btn");
            if (btn) btn.style.opacity = "1";
          },
          onMouseLeave: e => {
            const btn = e.currentTarget.querySelector(".backup-download-btn");
            if (btn) btn.style.opacity = "0";
          },
        })}
      />
    </div>
  );

  const mobileCardsBlock = (
    <Spin spinning={loading}>
      <div className="company-backups-mobile-list">
        {filtered.map(record => (
          <div key={record.id} className="company-backups-card">
            <div className="company-backups-card-row1">
              <span className="company-backups-card-company">{record.companyName ?? "—"}</span>
              <div className="company-backups-card-row1-tail">
                <span className="company-backups-card-status">{renderStatusTag(record.status)}</span>
                {[2, 3, 4].includes(record.status) && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownload(record.id)}
                    aria-label="Скачать"
                    className="company-backups-card-download-btn"
                  />
                )}
              </div>
            </div>
            <div className="company-backups-card-row2">
              <span className="company-backups-card-dt">
                {formatCreatedOnMobile(record.createdOn)}
              </span>
              <span className="company-backups-card-sep" aria-hidden>
                ·
              </span>
              <span className="company-backups-card-id">#{record.id}</span>
              <span className="company-backups-card-sep" aria-hidden>
                ·
              </span>
              <span className="company-backups-card-metric">
                {formatMbNumber(record.sizeTake)} / {formatMbNumber(record.size)} M
              </span>
            </div>
          </div>
        ))}
      </div>
    </Spin>
  );

  return (
    <div className={isMobile ? "company-backups-root company-backups-root--mobile" : undefined}>
      {isMobile ? (
        <>
          <div className="company-backups-mobile-search-row">
            <Input
              placeholder="Поиск (Компания, Код, #)"
              value={searchStr}
              onChange={e => setSearchStr(e.target.value)}
              allowClear
              className="company-backups-mobile-search-input"
            />
            <Button
              type="default"
              icon={<ReloadOutlined />}
              onClick={getData}
              loading={loading}
              disabled={!dateFrom || !dateTo}
              aria-label="Обновить"
              className="company-backups-mobile-refresh-btn"
            />
          </div>
          <div className="company-backups-mobile-filter-row">
            <span className="company-backups-mobile-filter-summary" title={filterSummary}>
              {filterSummary}
            </span>
            <Button
              type="default"
              icon={<FilterOutlined />}
              onClick={openFilterModal}
              aria-label="Фильтр"
              className="company-backups-mobile-filter-icon-btn"
            />
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">С даты</span>
                    <DatePicker
                      value={draftDateFrom}
                      onChange={setDraftDateFrom}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">По дату</span>
                    <DatePicker
                      value={draftDateTo}
                      onChange={setDraftDateTo}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  {user?.admin && (
                    <div className="company-backups-filter-field company-backups-filter-switch-row">
                      <span>По всем компаниям</span>
                      <Checkbox checked={draftAllCompanies} onChange={e => setDraftAllCompanies(e.target.checked)} />
                    </div>
                  )}
                  <div className="company-backups-filter-field company-backups-filter-switch-row">
                    <span>Только активные</span>
                    <Checkbox checked={draftOnlyActive} onChange={e => setDraftOnlyActive(e.target.checked)} />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="company-backups-mobile-scroll">{mobileCardsBlock}</div>
        </>
      ) : (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder="Поиск (Компания, Код, #)"
              value={searchStr}
              onChange={e => setSearchStr(e.target.value)}
              style={{ width: 250 }}
            />
            <DatePicker
              value={dateFrom}
              onChange={handleDateFromChange}
              format="DD.MM.YYYY"
              allowClear={false}
              style={{ width: 150 }}
              placeholder="С"
            />
            <DatePicker
              value={dateTo}
              onChange={handleDateToChange}
              format="DD.MM.YYYY"
              allowClear={false}
              style={{ width: 150 }}
              placeholder="По"
            />
            {user?.admin && (
              <Checkbox checked={allCompanies} onChange={e => setAllCompanies(e.target.checked)}>
                По всем компаниям
              </Checkbox>
            )}
            <Checkbox checked={onlyActive} onChange={e => setOnlyActive(e.target.checked)}>
              Только активные
            </Checkbox>
            {refreshButton(getData)}
          </Space>
          {desktopTableBlock}
        </>
      )}
    </div>
  );
};

export default CompanyBackups;
